/**
 * QMS Filters Hook
 * Manages filter state and URL synchronization for the QMS board
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  QmsWorkflowFilter,
  QmsTicketType,
  QmsTicket,
  QmsBoardResponse,
  QmsPendingCounts,
} from '../types/qms.types';
import { isPopulatedUser } from '../types/qms.types';

interface UseQmsFiltersOptions {
  boardData: QmsBoardResponse | undefined;
}

export type QmsTypeFilter = 'all' | QmsTicketType;

interface UseQmsFiltersResult {
  // Filter state
  typeFilter: QmsTypeFilter;
  workflowFilter: QmsWorkflowFilter;
  searchQuery: string;
  assigneeFilter: string;
  accountFilter: string;
  departmentFilter: string;

  // Filter setters
  setTypeFilter: (filter: QmsTypeFilter) => void;
  setWorkflowFilter: (filter: QmsWorkflowFilter) => void;
  setSearchQuery: (query: string) => void;
  setAssigneeFilter: (assigneeId: string) => void;
  setAccountFilter: (accountId: string) => void;
  setDepartmentFilter: (department: string) => void;

  // Derived data
  filteredBoard: Record<string, QmsTicket[]>;
  pendingCounts: QmsPendingCounts;

  // Actions
  clearAllFilters: () => void;
}

const VALID_WORKFLOW_FILTERS: QmsWorkflowFilter[] = [
  'all',
  'pending_qd_approval',
  'qd_approved',
  'pending_ceo_review',
  'ceo_confirmed',
];

/**
 * Hook for managing QMS board filters with URL sync
 */
export function useQmsFilters({ boardData }: UseQmsFiltersOptions): UseQmsFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const getInitialWorkflowFilter = (): QmsWorkflowFilter => {
    const filterParam = searchParams.get('filter');
    return VALID_WORKFLOW_FILTERS.includes(filterParam as QmsWorkflowFilter)
      ? (filterParam as QmsWorkflowFilter)
      : 'all';
  };

  const getInitialAssigneeFilter = (): string => {
    return searchParams.get('assignee') || 'all';
  };

  const getInitialAccountFilter = (): string => {
    return searchParams.get('account') || 'all';
  };

  const getInitialDepartmentFilter = (): string => {
    return searchParams.get('department') || 'all';
  };

  const getInitialTypeFilter = (): QmsTypeFilter => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'ticket' || typeParam === 'capa' || typeParam === 'client') return typeParam;
    return 'all';
  };

  const [typeFilter, setTypeFilter] = useState<QmsTypeFilter>(getInitialTypeFilter);
  const [workflowFilter, setWorkflowFilter] = useState<QmsWorkflowFilter>(getInitialWorkflowFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>(getInitialAssigneeFilter);
  const [accountFilter, setAccountFilter] = useState<string>(getInitialAccountFilter);
  const [departmentFilter, setDepartmentFilter] = useState<string>(getInitialDepartmentFilter);

  // Sync URL params when filters change
  useEffect(() => {
    let shouldUpdate = false;
    const currentFilter = searchParams.get('filter');
    const currentAssignee = searchParams.get('assignee');

    // Handle workflow filter
    if (workflowFilter === 'all') {
      if (currentFilter) {
        searchParams.delete('filter');
        shouldUpdate = true;
      }
    } else if (currentFilter !== workflowFilter) {
      searchParams.set('filter', workflowFilter);
      shouldUpdate = true;
    }

    // Handle assignee filter
    if (assigneeFilter === 'all') {
      if (currentAssignee) {
        searchParams.delete('assignee');
        shouldUpdate = true;
      }
    } else if (currentAssignee !== assigneeFilter) {
      searchParams.set('assignee', assigneeFilter);
      shouldUpdate = true;
    }

    // Handle account filter
    const currentAccount = searchParams.get('account');
    if (accountFilter === 'all') {
      if (currentAccount) {
        searchParams.delete('account');
        shouldUpdate = true;
      }
    } else if (currentAccount !== accountFilter) {
      searchParams.set('account', accountFilter);
      shouldUpdate = true;
    }

    // Handle type filter
    const currentType = searchParams.get('type');
    if (typeFilter === 'all') {
      if (currentType) {
        searchParams.delete('type');
        shouldUpdate = true;
      }
    } else if (currentType !== typeFilter) {
      searchParams.set('type', typeFilter);
      shouldUpdate = true;
    }

    // Handle department filter
    const currentDepartment = searchParams.get('department');
    if (departmentFilter === 'all') {
      if (currentDepartment) {
        searchParams.delete('department');
        shouldUpdate = true;
      }
    } else if (currentDepartment !== departmentFilter) {
      searchParams.set('department', departmentFilter);
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [workflowFilter, assigneeFilter, accountFilter, typeFilter, departmentFilter, searchParams, setSearchParams]);

  // Calculate pending counts
  const pendingCounts: QmsPendingCounts = useMemo(() => {
    if (!boardData?.columns) return { qd_approval: 0, ceo_review: 0 };

    let qdCount = 0;
    let ceoCount = 0;

    Object.values(boardData.columns).forEach((tickets) => {
      tickets.forEach((ticket) => {
        if (ticket.qd_approval_status === 'pending') {
          qdCount++;
        }
        if (ticket.requires_ceo_review && ticket.ceo_review_status === 'pending') {
          ceoCount++;
        }
      });
    });

    return { qd_approval: qdCount, ceo_review: ceoCount };
  }, [boardData]);

  // Owner + team option lists are now sourced externally (Employees directory
  // + Teams). The page passes them straight to QmsFilterBar, so this hook
  // only owns filter state + filter logic.

  // Apply filters to board data
  const filteredBoard = useMemo((): Record<string, QmsTicket[]> => {
    if (!boardData?.columns) return {};

    const filtered: Record<string, QmsTicket[]> = {};
    const normalizedSearch = searchQuery.toLowerCase().trim();

    Object.entries(boardData.columns).forEach(([key, tickets]) => {
      filtered[key] = tickets.filter((ticket) => {
        // Apply workflow filter
        let passesWorkflowFilter = true;
        if (workflowFilter !== 'all') {
          switch (workflowFilter) {
            case 'pending_qd_approval':
              passesWorkflowFilter = ticket.qd_approval_status === 'pending';
              break;
            case 'qd_approved':
              passesWorkflowFilter =
                ticket.qd_approval_status === 'approved' && !ticket.requires_ceo_review;
              break;
            case 'pending_ceo_review':
              passesWorkflowFilter =
                !!ticket.requires_ceo_review && ticket.ceo_review_status === 'pending';
              break;
            case 'ceo_confirmed':
              passesWorkflowFilter =
                ticket.ceo_review_status === 'confirmed' ||
                ticket.ceo_review_status === 'priority_adjusted';
              break;
            default:
              passesWorkflowFilter = true;
          }
        }

        // Apply search filter
        let passesSearch = true;
        if (normalizedSearch) {
          const assigneeName = isPopulatedUser(ticket.assigned_to)
            ? `${ticket.assigned_to.firstname} ${ticket.assigned_to.lastname}`.toLowerCase()
            : '';
          // Format ticket number as QUAL-31, CAPA-31, or CLT-31
          const ticketPrefix = ticket.type === 'capa' ? 'capa' : ticket.type === 'client' ? 'clt' : 'qual';
          const ticketNumber = ticket.ticket_number
            ? `${ticketPrefix}-${ticket.ticket_number}`
            : '';
          const searchableText = [
            ticket.title?.toLowerCase() || '',
            ticket.description?.toLowerCase() || '',
            ticket._id?.toLowerCase() || '',
            assigneeName,
            ticketNumber,
          ].join(' ');
          passesSearch = searchableText.includes(normalizedSearch);
        }

        // Apply assignee filter
        let passesAssigneeFilter = true;
        if (assigneeFilter !== 'all') {
          if (assigneeFilter === 'unassigned') {
            passesAssigneeFilter = !ticket.assigned_to;
          } else {
            const assigneeId = isPopulatedUser(ticket.assigned_to)
              ? ticket.assigned_to._id
              : ticket.assigned_to;
            passesAssigneeFilter = assigneeId === assigneeFilter;
          }
        }

        // Apply account filter
        let passesAccountFilter = true;
        if (accountFilter !== 'all') {
          if (accountFilter === 'unassigned') {
            passesAccountFilter = !ticket.account_id;
          } else {
            passesAccountFilter = ticket.account_id === accountFilter;
          }
        }

        // Apply team filter (multi-team membership via team_ids JSONB).
        // Empty team_ids = visible to everyone, so falls through unless
        // the user is filtering on a specific team.
        let passesDepartmentFilter = true;
        if (departmentFilter !== 'all') {
          const ticketTeamIds = Array.isArray(ticket.team_ids) ? ticket.team_ids : [];
          passesDepartmentFilter = ticketTeamIds.includes(departmentFilter);
        }

        return passesWorkflowFilter && passesSearch && passesAssigneeFilter && passesAccountFilter && passesDepartmentFilter;
      });
    });

    return filtered;
  }, [boardData, workflowFilter, searchQuery, assigneeFilter, accountFilter, departmentFilter]);

  // Clear all filters
  const clearAllFilters = () => {
    setTypeFilter('all');
    setWorkflowFilter('all');
    setSearchQuery('');
    setAssigneeFilter('all');
    setAccountFilter('all');
    setDepartmentFilter('all');
  };

  return {
    typeFilter,
    workflowFilter,
    searchQuery,
    assigneeFilter,
    accountFilter,
    departmentFilter,
    setTypeFilter,
    setWorkflowFilter,
    setSearchQuery,
    setAssigneeFilter,
    setAccountFilter,
    setDepartmentFilter,
    filteredBoard,
    pendingCounts,
    clearAllFilters,
  };
}
